import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { permissionService } from '@services';
import useAuthStore from '@store/useAuthStore';

/**
 * Hook para obtener todos los permisos del sistema
 */
export const useAllPermissions = (options = {}) => {
  return useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutos - los permisos no cambian frecuentemente
    ...options,
  });
};

/**
 * Hook para obtener los módulos de permisos
 */
export const usePermissionModules = (options = {}) => {
  return useQuery({
    queryKey: ['permissionModules'],
    queryFn: () => permissionService.getModules(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook principal para verificar permisos del usuario actual
 * Usa los permisos almacenados en el store de autenticación
 */
export const usePermission = () => {
  const { permissions } = useAuthStore();

  const hasPermission = useCallback(
    (permission) => {
      if (!permissions || permissions.length === 0) return false;

      // Admin tiene todos los permisos
      if (permissions.some((p) => p.startsWith('admin.'))) return true;

      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (permissionList) => {
      return permissionList.some((perm) => hasPermission(perm));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissionList) => {
      return permissionList.every((perm) => hasPermission(perm));
    },
    [hasPermission]
  );

  const canAccess = useCallback(
    (module, action) => {
      return hasPermission(`${module}.${action}`);
    },
    [hasPermission]
  );

  // Permisos agrupados por módulo para facilitar uso en UI
  const permissionsByModule = useMemo(() => {
    if (!permissions) return {};

    return permissions.reduce((acc, perm) => {
      const [module] = perm.split('.');
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(perm);
      return acc;
    }, {});
  }, [permissions]);

  return {
    permissions,
    permissionsByModule,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
  };
};

export default usePermission;
